import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../redux-store";
import {
  DbKalikoWorkflowExecInfos,
  DbKalikoWorkflowModelInfos,
  IKalikoNode,
  KalikoNodeCallJobRule,
  KalikoNodeCallWorkflow,
  KalikoNodeGatewayBase,
  KalikoNodeGetMetadata,
  KalikoNodeSetMetadata,
  KalikoNodeStartEndEventBase,
  KalikoNodeTimer,
  KalikoNodeType,
  KalikoWorkflowVariable,
} from "../../Core/Api/Types/kaliko";
import { addEdge, applyEdgeChanges, applyNodeChanges, EdgeChange, NodeChange, OnEdgesChange } from "@xyflow/react";
import { KalikoLinkProps } from "../../Pages/admin/Settings/SettingsDiagramDesigner/edge";
import { KalikoNodeProps } from "../../Pages/admin/Settings/SettingsDiagramDesigner/kaliko-node";
import { WritableDraft } from "immer";
import { ElkNode } from "elkjs/lib/elk-api";
import { v4 as uuidv4 } from "uuid";
export interface WorkflowDesignerState {
  isNewWorkflow: boolean;
  workflow: DbKalikoWorkflowModelInfos;
  nodes: (
    | KalikoNodeCallJobRule
    | KalikoNodeCallWorkflow
    | KalikoNodeGatewayBase
    | KalikoNodeGetMetadata
    | KalikoNodeSetMetadata
    | KalikoNodeStartEndEventBase
    | KalikoNodeTimer
  )[];
  selectedNodeType: KalikoNodeType | null;
  diagramNodes: KalikoNodeProps[];
  diagramEdges: KalikoLinkProps[];
  selectedNode: string[];
  selectedEdge: string[];
  loadingNodes: boolean;
  loadingWorkflow: boolean;
  workflows: DbKalikoWorkflowModelInfos[];
  fitViewTrigger: number;
  autoLayoutTrigger: number;
  orientation: "horizontal" | "vertical";
}
const initialState: WorkflowDesignerState = {
  isNewWorkflow: true,
  workflow: {
    id: -500,
    created: null,
    updated: null,
    description: "",
    enabled: true,
    model: {
      links: [],
      nodes: [],
      parallelGatewayAbortNodeIfAnyBranchFailed: true,
      parallelGatewayContinueBranchesInCaseOfBranchFailed: false,
      retries: 0,
      subscribeOnNodeChanges: false,
      vars: [],
      abstractWorkflowId: undefined,
      kalikoExecWorkflowId: undefined,
      masterKalikoExecWorkflowId: undefined,
      ownerUserId: undefined,
    },
    name: "",
    version: 0,
  },
  nodes: [],
  selectedNodeType: null,
  diagramNodes: [],
  diagramEdges: [],
  selectedNode: [],
  selectedEdge: [],
  loadingNodes: false,
  loadingWorkflow: false,
  workflows: [],
  fitViewTrigger: 0,
  autoLayoutTrigger: 0,
  orientation: "horizontal",
};

export const workflowDesignerSlice = createSlice({
  name: "workflowDesigner",
  initialState,
  reducers: {
    InitWorkflowDesigner: (state) => {
      return { ...initialState };
    },
    InitNewWorkflow: (state) => {
      state.workflow = initialState.workflow;
      state.diagramEdges = [];
      state.diagramNodes = [];
      state.loadingNodes = false;
      state.loadingWorkflow = false;
      state.isNewWorkflow = true;
      state.selectedEdge = [];
      state.selectedNode = [];
    },
    SetIsNewWorkflow: (state, action: PayloadAction<boolean>) => {
      state.isNewWorkflow = action.payload;
    },
    SetWorkflow: (state, action: PayloadAction<DbKalikoWorkflowModelInfos>) => {
      state.workflow = action.payload;
    },
    SetNodes: (state, action: PayloadAction<any>) => {
      state.nodes = action.payload;
    },
    SetSelectedNodeType: (state, action: PayloadAction<KalikoNodeType | null>) => {
      state.selectedNodeType = action.payload;
    },
    SetDiagramNodes: (state, action: PayloadAction<KalikoNodeProps[]>) => {
      state.diagramNodes = action.payload;
    },
    DeleteDiagramNodeById: (state, action: PayloadAction<string>) => {
      state.diagramNodes = state.diagramNodes.filter((x) => x.id !== action.payload);
    },
    AddDiagramNode: (state, action: PayloadAction<KalikoNodeProps>) => {
      state.diagramNodes = [...state.diagramNodes, action.payload];
    },
    SetDiagramEdges: (state, action: PayloadAction<KalikoLinkProps[]>) => {
      state.diagramEdges = action.payload;
    },
    DeleteDiagramEdgeById: (state, action: PayloadAction<string>) => {
      state.diagramEdges = state.diagramEdges.filter((x) => x.id !== action.payload);
    },
    AddDiagramEdge: (state, action: PayloadAction<KalikoLinkProps>) => {
      state.diagramEdges = [...state.diagramEdges, action.payload];
    },
    SetSelectedNodes: (state, action: PayloadAction<string[]>) => {
      state.selectedNode = action.payload;
    },
    SetSelectedEdges: (state, action: PayloadAction<string[]>) => {
      state.selectedEdge = action.payload;
    },
    UpdateNodes: (state, action: PayloadAction<NodeChange<WritableDraft<KalikoNodeProps>>[]>) => {
      state.diagramNodes = applyNodeChanges(action.payload, state.diagramNodes);
    },
    UpdateEdges: (state, action: PayloadAction<EdgeChange<WritableDraft<KalikoLinkProps>>[]>) => {
      state.diagramEdges = applyEdgeChanges(action.payload, state.diagramEdges);
    },
    UpdateWorkflow: (state, action: PayloadAction<any>) => {
      if (!state.workflow) return;
      Object.keys(action.payload).forEach((key) => {
        if (state.workflow) state.workflow[key] = action.payload[key];
      });
      state.workflow = { ...state.workflow };
    },
    UpdateWorkflowModel: (state, action: PayloadAction<any>) => {
      if (!state.workflow || !state.workflow.model) return;
      Object.keys(action.payload).forEach((key) => {
        if (state.workflow && state.workflow.model) state.workflow.model[key] = action.payload[key];
      });
      state.workflow = { ...state.workflow };
    },
    LoadWorkflow: (state, action: PayloadAction<DbKalikoWorkflowModelInfos>) => {
      if (!action.payload.model?.nodes) return;
      state.orientation = calculateOrientation(action.payload.model.nodes);
      state.workflow = action.payload;
      state.diagramNodes = state.workflow.model.nodes.map((x, i) => ({
        data: { ...x },
        id: i.toString(),
        type: "kalikoNode",
        position: x.position ?? { x: 0, y: 0 },
      }));
      state.diagramEdges = state.workflow.model.links.map((x, i) => ({
        target: state.diagramNodes.find((n) => n.data.uid === x.targetUid)!.id,
        source: state.diagramNodes.find((n) => n.data.uid === x.sourceUid)!.id,
        data: { condition: x.condition, name: x.name ?? "" },
        id: i.toString(),
      }));
      state.isNewWorkflow = false;
      state.fitViewTrigger += 1;
      if (
        action.payload.model &&
        action.payload.model.nodes?.length > 0 &&
        !action.payload.model.nodes.some((x) => x.position)
      ) {
        state.autoLayoutTrigger += 1;
      }
    },
    LoadModelFromExec: (state, action: PayloadAction<DbKalikoWorkflowExecInfos>) => {
      state.workflow.name = action.payload.name;
      if (!action.payload.model?.nodes) return;
      state.orientation = calculateOrientation(action.payload.model.nodes);
      state.diagramNodes = action.payload.model.nodes.map((x, i) => ({
        data: { ...x },
        id: i.toString(),
        type: "kalikoNode",
        position: x.position ?? { x: 0, y: 0 },
      }));
      state.diagramEdges = action.payload.model.links.map((x, i) => ({
        target: state.diagramNodes.find((n) => n.data.uid === x.targetUid)!.id,
        source: state.diagramNodes.find((n) => n.data.uid === x.sourceUid)!.id,
        data: { condition: x.condition, name: x.name ?? "" },
        id: i.toString(),
      }));
      state.isNewWorkflow = false;

      if (
        action.payload.model &&
        action.payload.model.nodes?.length > 0 &&
        !action.payload.model.nodes.some((x) => x.position)
      ) {
        state.autoLayoutTrigger += 1;
      }
    },
    SetLoadingNodes: (state, action: PayloadAction<boolean>) => {
      state.loadingNodes = action.payload;
    },
    SetLoadingWorkflow: (state, action: PayloadAction<boolean>) => {
      state.loadingWorkflow = action.payload;
    },
    AddEdge: (state, action) => {
      state.diagramEdges = addEdge(action.payload, state.diagramEdges);
    },
    SetWorkflows: (state, action: PayloadAction<DbKalikoWorkflowModelInfos[]>) => {
      state.workflows = action.payload;
    },
    AddWorkflow: (state, action: PayloadAction<DbKalikoWorkflowModelInfos>) => {
      state.workflows = [...state.workflows, action.payload];
    },
    ProcessAutoLayout: (state, action: PayloadAction<ElkNode[]>) => {
      if (!action.payload || !action.payload.length) return;
      state.diagramNodes = state.diagramNodes.map((x) => ({
        ...x,
        position: {
          x:
            state.orientation === "horizontal"
              ? action.payload.find((n) => n.id === x.id)?.x ?? x.position?.x
              : action.payload.find((n) => n.id === x.id)?.y ?? x.position?.y,
          y:
            state.orientation === "horizontal"
              ? action.payload.find((n) => n.id === x.id)?.y ?? x.position.y
              : action.payload.find((n) => n.id === x.id)?.x ?? x.position?.x,
        },
      }));

      state.fitViewTrigger += 1;
    },
    TriggerAutoLayout: (state) => {
      state.autoLayoutTrigger += 1;
    },
    SetOrientation: (state, action: PayloadAction<"horizontal" | "vertical">) => {
      if (action.payload !== state.orientation) {
        state.orientation = action.payload;
        state.diagramNodes = state.diagramNodes.map((x) => ({ ...x, position: { x: x.position.y, y: x.position.x } }));
        state.diagramNodes = [...state.diagramNodes];
      }
    },
    DeleteWorkflowById: (state, action: PayloadAction<number>) => {
      const id = action.payload;
      state.workflows = state.workflows.filter((x) => x.id !== id);
      if (state.workflow.id === id) {
        state.workflow = initialState.workflow;
        state.diagramEdges = [];
        state.diagramNodes = [];
        state.loadingNodes = false;
        state.loadingWorkflow = false;
        state.isNewWorkflow = true;
        state.selectedEdge = [];
        state.selectedNode = [];
      }
    },
    UpdateWorkflowVars: (state, action: PayloadAction<{ v: KalikoWorkflowVariable; index: number }>) => {
      const { index, v } = action.payload;
      const varToUpdate = state.workflow.model.vars[index];
      if (varToUpdate) {
        varToUpdate.name = v.name;
        varToUpdate.value = v.value;
      }
    },
    AddGlobalVar: (state) => {
      const newVar = { name: "", value: "" };
      state.workflow.model.vars = [...state.workflow.model.vars, newVar];
    },
    RemoveGlobalVarByIndex: (state, action: PayloadAction<number>) => {
      state.workflow.model.vars = [...state.workflow.model.vars.filter((x, i) => i !== action.payload)];
    },
    AddCurrentNodeType: (state) => {
      const id = uuidv4();

      const newNodeData = state.nodes.find((x) => x.type === state.selectedNodeType);
      const currentTypeCount = state.diagramNodes.filter((x) => x.data.type === state.selectedNodeType)?.length;

      const newNode: KalikoNodeProps = {
        id,
        position: { x: 0, y: 0 },
        data: {
          ...newNodeData,
          inputParams: newNodeData?.defaultInputParams,
          uid: id,
          label: newNodeData?.systemName ?? "UNKNOWN",
          name: newNodeData?.systemName + (currentTypeCount ? " " + (currentTypeCount + 1).toString() : ""),
        },
        origin: [0.5, 0.0],
        type: "kalikoNode",
      };

      state.diagramNodes = [...state.diagramNodes, newNode];
    },
  },
});

export const {
  InitWorkflowDesigner,
  InitNewWorkflow,
  SetIsNewWorkflow,
  SetWorkflow,
  SetNodes,
  SetSelectedNodeType,
  DeleteDiagramNodeById,
  SetDiagramNodes,
  AddDiagramNode,
  AddDiagramEdge,
  DeleteDiagramEdgeById,
  SetDiagramEdges,
  SetSelectedEdges,
  SetSelectedNodes,
  UpdateNodes,
  UpdateEdges,
  UpdateWorkflow,
  UpdateWorkflowModel,
  LoadWorkflow,
  SetLoadingNodes,
  SetLoadingWorkflow,
  AddEdge,
  LoadModelFromExec,
  SetWorkflows,
  AddWorkflow,
  ProcessAutoLayout,
  TriggerAutoLayout,
  SetOrientation,
  DeleteWorkflowById,
  AddGlobalVar,
  RemoveGlobalVarByIndex,
  UpdateWorkflowVars,
  AddCurrentNodeType,
} = workflowDesignerSlice.actions;

export const GetNodes = (state: RootState) => state.workflowDesignerSlice.nodes;
export const GetIsNewWorkflow = (state: RootState) => state.workflowDesignerSlice.isNewWorkflow;
export const GetWorkflow = (state: RootState) => state.workflowDesignerSlice.workflow;
export const GetSelectedNodeType = (state: RootState) => state.workflowDesignerSlice.selectedNodeType;
export const GetDiagramNodes = (state: RootState) => state.workflowDesignerSlice.diagramNodes;
export const GetDiagramEdges = (state: RootState) => state.workflowDesignerSlice.diagramEdges;
export const GetSelectedNodes = (state: RootState) => state.workflowDesignerSlice.selectedNode;
export const GetSelectedEdges = (state: RootState) => state.workflowDesignerSlice.selectedEdge;
export const GetLoadingWorkflow = (state: RootState) => state.workflowDesignerSlice.loadingWorkflow;
export const GetLoadingNodes = (state: RootState) => state.workflowDesignerSlice.loadingNodes;
export const GetWorkflows = (state: RootState) => state.workflowDesignerSlice.workflows;
export const GetFitViewTrigger = (state: RootState) => state.workflowDesignerSlice.fitViewTrigger;
export const GetAutoLayoutTrigger = (state: RootState) => state.workflowDesignerSlice.autoLayoutTrigger;
export const GetOrientation = (state: RootState) => state.workflowDesignerSlice.orientation;

export default workflowDesignerSlice.reducer;

const calculateOrientation: (nodes: IKalikoNode[]) => "horizontal" | "vertical" = (nodes: IKalikoNode[]) => {
  if (nodes.some((n) => !n.position)) return "horizontal";

  const maxX = nodes.reduce((acc, n) => Math.max(acc, n.position!.x), -9999);
  const maxY = nodes.reduce((acc, n) => Math.max(acc, n.position!.y), -9999);
  const minX = nodes.reduce((acc, n) => Math.min(acc, n.position!.x), 9999);
  const minY = nodes.reduce((acc, n) => Math.min(acc, n.position!.y), 9999);

  const diffX = maxX - minX;
  const diffY = maxY - minY;

  if (diffX >= diffY) return "horizontal";
  else return "vertical";
};
